import {
  IColumn,
  Shimmer,
  ShimmeredDetailsList,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { DateTimeWithDurationSinceTooltip } from "@components/DateTimeWithDurationSinceTooltip";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { FieldDeviceSetting } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useFieldDeviceSettings,
  useFieldDeviceSettingsRefData
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { RefDataDto } from "@libs/api/types/common-dtos";

import { DeviceSettingRowDto } from "./";
import { DeviceSettingsFilterValues } from "./DeviceSettingsFilter";
import { DeviceSettingsValueField } from "./DeviceSettingsValueField";

interface DeviceSettingsTableProps {
  deviceId: string;
  filter: DeviceSettingsFilterValues;
}

export const DeviceSettingsTable = ({
  deviceId,
  filter
}: DeviceSettingsTableProps) => {
  const settingsQuery = useFieldDeviceSettings(deviceId);
  const settingsTypesQuery = useFieldDeviceSettingsRefData();

  const columns: IColumn[] = [
    {
      key: "key",
      name: "Key",
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      isSorted: true,
      onRender: (setting: DeviceSettingRowDto) => (
        <TooltipHost content={setting.settingTypeDescription}>
          {setting.fieldDeviceSettingType}
        </TooltipHost>
      )
    },
    {
      key: "value",
      name: "Value",
      minWidth: 100,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => setting.value
    },
    {
      key: "updateValue",
      name: "Update Value",
      minWidth: 300,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => (
        <DeviceSettingsValueField item={setting} />
      )
    },
    {
      key: "createdDate",
      name: "Created Date",
      minWidth: 150,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={setting.changeLog?.createdDate}
        />
      )
    },
    {
      key: "createdBy",
      name: "Created By",
      minWidth: 150,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => setting.changeLog?.createdBy
    },
    {
      key: "updatedDate",
      name: "Last Updated Date",
      minWidth: 150,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => (
        <DateTimeWithDurationSinceTooltip
          dateUtc={setting.changeLog?.updatedDate}
        />
      )
    },
    {
      key: "updatedBy",
      name: "Last Updated By",
      minWidth: 150,
      isResizable: true,
      onRender: (setting: DeviceSettingRowDto) => setting.changeLog?.updatedBy
    }
  ];

  return (
    <QueryStateIndicator
      data={{
        settings: settingsQuery.data,
        settingsTypes: settingsTypesQuery.data
      }}
      isLoading={settingsQuery?.isLoading || settingsTypesQuery?.isLoading}
      error={settingsQuery?.error ?? settingsTypesQuery?.error}
      LoadingComponent={<Shimmer />}
    >
      {({ settings, settingsTypes }) => {
        const combinedItems = getCombinedItems(
          deviceId,
          settings,
          settingsTypes
        );

        return (
          <Stack
            verticalFill
            styles={{
              root: {
                overflowY: "auto"
              }
            }}
          >
            <ShimmeredDetailsList
              stickyHeader
              shimmerLines={10}
              items={filterAndSortSettings(combinedItems, filter)}
              columns={columns}
              onShouldVirtualize={() => true}
            />
          </Stack>
        );
      }}
    </QueryStateIndicator>
  );
};

const filterAndSortSettings = (
  claims: DeviceSettingRowDto[] | undefined,
  filter: DeviceSettingsFilterValues
): DeviceSettingRowDto[] => {
  if (!claims) return [];

  return claims
    .filter(
      d =>
        filter.name === "" ||
        d.fieldDeviceSettingType
          .toLowerCase()
          .indexOf(filter.name.toLowerCase()) >= 0 ||
        (d.value &&
          d.value.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0)
    )
    .sort((a: DeviceSettingRowDto, b: DeviceSettingRowDto) => {
      return a.fieldDeviceSettingType < b.fieldDeviceSettingType ? -1 : 1;
    });
};

function getCombinedItems(
  fieldDeviceId: string,
  settings?: FieldDeviceSetting[],
  settingsTypes?: RefDataDto<string>[]
): DeviceSettingRowDto[] {
  return (
    settingsTypes?.map(settingsType => {
      const setting = settings?.find(
        x => x.fieldDeviceSettingType === settingsType.code
      );

      return {
        ...setting,
        fieldDeviceId,
        fieldDeviceSettingType: settingsType.code,
        settingTypeDescription: settingsType.text
      } as DeviceSettingRowDto;
    }) ?? []
  );
}
